import { FACILITY_BY_ID, GOOD_BY_ID, PLOT_OPTIONS, RANKS } from './data.js';

import { formatCurrency as money } from './currency.js';

// Benefits describe actual simulation capabilities, never promised earnings.
export function buildingBenefits(facility, current = null) {
  if (!facility) return [];
  const benefits = [];
  if (facility.kind === 'shop') {
    benefits.push(`${facility.displaySlots} shelf displays${current ? `, up from ${current.displaySlots || 0}` : ''}`);
    benefits.push(`${facility.capacity} storage${current ? `, up from ${current.capacity || 0}` : ''}`);
  }
  if (facility.serviceCents) benefits.push(`Base visitor fee ${money(facility.serviceCents)}; actual income depends on arrivals, condition and the daily service limit`);
  if (facility.id === 'canoe-landing') benefits.push('Opens level 1 Chicago freight');
  if (facility.marketAccessGoodIds?.length) benefits.push(`Opens ${facility.marketAccessGoodIds.map(id => GOOD_BY_ID[id]?.name || id).join(', ')} trading`);
  if (facility.garden) benefits.push(`Grows ${GOOD_BY_ID[facility.garden.goodId]?.name || facility.garden.goodId}; yield follows the season and plot condition`);
  if (facility.production) {
    const names = bundle => Object.entries(bundle).map(([id, qty]) => `${qty} ${GOOD_BY_ID[id]?.name || id}`).join(' + ');
    benefits.push(`${names(facility.production.inputs)} → ${names(facility.production.outputs)} every ${facility.production.cycleDays} day${facility.production.cycleDays === 1 ? '' : 's'}`);
  }
  if (facility.freightDiscountBps) benefits.push(`${facility.freightDiscountBps / 100}% freight discount`);
  return benefits;
}

export function townReport(state, derived) {
  const today = state.ledger.filter(entry => entry.day === derived.day);
  const partial = today.length > 0 && today[0].id !== 'ledger-1' && state.ledger[0]?.day === derived.day;
  const active = derived.plots.filter(plot => plot.owned && plot.facilityId);
  const properties = active.map(plot => {
    const facility = FACILITY_BY_ID[plot.facilityId];
    const fees = today.filter(entry => entry.accepted && (facility.kind === 'shop'
      ? ['sell', 'meeting-sell'].includes(entry.kind)
      : entry.kind === 'service' && entry.plotIndex === plot.index));
    return {
      plotIndex: plot.index, name: facility.name, kind: facility.kind,
      incomeCents: fees.reduce((sum, entry) => sum + (entry.totalCents || 0), 0),
      visits: fees.length, upkeepCents: facility.upkeepCents || 0,
      condition: plot.condition, construction: plot.construction, benefits: buildingBenefits(facility),
    };
  });
  const nextRank = RANKS.find(rank => rank.level > state.rank);
  const rankNeeds = nextRank ? [
    { label: 'Reputation', current: state.reputation, target: nextRank.reputation },
    { label: 'Completed trades', current: state.transactions, target: nextRank.trades },
    { label: 'Town day', current: derived.day, target: nextRank.day },
  ] : [];
  return { properties, serviceIncomeCents: today.filter(entry => entry.accepted && entry.kind === 'service').reduce((sum, entry) => sum + (entry.totalCents || 0), 0), partial, nextRank, rankNeeds };
}

// Goals open a comparison. Reading this plan never spends or changes a town.
export function growthChoices(state, derived) {
  const shop = derived.plots.find(plot => plot.index === 3);
  const currentShop = FACILITY_BY_ID[shop.facilityId];
  const nextShop = ['shop-general', 'shop-emporium', 'shop-trading-house'].map(id => FACILITY_BY_ID[id]).find(f => f.tier > currentShop.tier);
  const riverUpgrade = derived.plots.find(plot => plot.owned && !plot.construction
    && ['wild-landing', 'marked-trail'].includes(plot.facilityId)
    && PLOT_OPTIONS[plot.index].includes('canoe-landing'));
  const riverBefore = FACILITY_BY_ID[riverUpgrade?.facilityId];
  const options = [
    nextShop && { facility: nextShop, plot: shop, title: 'Grow your shop', purpose: 'Carry a wider assortment and keep more stock.' },
    { facility: FACILITY_BY_ID['canoe-landing'], plot: riverUpgrade, title: 'Welcome river trade', purpose: riverUpgrade
      ? `Replace ${riverBefore.name} on your owned Plot ${riverUpgrade.index}. Raise the base visitor fee from ${money(riverBefore.serviceCents || 0)} to 70 silver coins and open Chicago freight. Decorations stay.`
      : 'Add visitor fees and open more freight.' },
    { facility: FACILITY_BY_ID['market-garden'], title: 'Grow your own goods', purpose: 'Produce herbs for your counter as the seasons change.' },
  ].filter(Boolean);
  return options.flatMap(option => {
    const facility = option.facility;
    if (derived.plots.some(plot => plot.owned && plot.facilityId === facility.id)) return [];
    const plot = option.plot || [...derived.plots].filter(p => p.index !== 3 && !p.facilityId && !p.construction && PLOT_OPTIONS[p.index].includes(facility.id))
      .sort((a, b) => Number(b.owned) - Number(a.owned) || a.deedCostCents - b.deedCostCents)[0];
    if (!plot) return [];
    const totalCents = facility.costCents + facility.laborCostCents + (plot.owned ? 0 : plot.deedCostCents);
    const reserveCents = 1000;
    const rank = RANKS.find(r => r.level === facility.rank);
    const requirements = facility.rank > state.rank ? `${rank?.name || `Rank ${facility.rank + 1}`} required` : plot.construction ? 'A crew is already working here' : state.cashCents < totalCents + reserveCents ? `${money(totalCents + reserveCents - state.cashCents)} more to keep ${money(reserveCents)} for trading` : 'Cash ready; compare the project before buying';
    return [{ ...option, plotIndex: plot.index, facilityId: facility.id, name: facility.name, totalCents, reserveCents, requirements, benefits: buildingBenefits(facility, facility.kind === 'shop' ? currentShop : null) }];
  });
}
